/**
 * IndexedDB utility for storing photos offline
 * Stores actual File objects (binary data) that persist across browser restarts
 */

import { captureException } from '@sentry/react'

type StoredPhotoBase = {
  id: string // Entity ID (callback, repair, survey, maintenance, test, oil-record, etc.)
  entityType:
    | 'callbacks'
    | 'repairs'
    | 'surveys'
    | 'maintenance'
    | 'tests'
    | 'oil-records'

  files: File[]
  blobUrls: string[] // Cached blob URLs for offline preview
  metadata: {
    companyId: string
    siteId: string
    unitId: string
    timestamp: number
    programId?: string // For maintenance and tests
  }
}

type StoredPhotoSubEntity = StoredPhotoBase & {
  subEntityType: 'repair' | 'replacement' | 'alteration' // For nested entities in repairs
  subEntityIndex: number // Index of the sub-entity in the array
}

type StoredPhoto = StoredPhotoBase | StoredPhotoSubEntity

// Input types for savePhotos - omit auto-generated fields
type SavePhotosInputBase = Omit<StoredPhotoBase, 'metadata' | 'blobUrls'> & {
  metadata: Omit<StoredPhotoBase['metadata'], 'timestamp'>
  blobUrls?: string[]
}

type SavePhotosInputSubEntity = Omit<
  StoredPhotoSubEntity,
  'metadata' | 'blobUrls'
> & {
  metadata: Omit<StoredPhotoSubEntity['metadata'], 'timestamp'>
  blobUrls?: string[]
}

type SavePhotosInput = SavePhotosInputBase | SavePhotosInputSubEntity

const DB_NAME = 'LiftAI_OfflinePhotos'
const DB_VERSION = 1
const STORE_NAME = 'pending_photos'

class OfflinePhotoStorage {
  private db: IDBDatabase | null = null

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION)

      request.onerror = () => reject(request.error)
      request.onsuccess = () => {
        this.db = request.result
        resolve()
      }

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' })
          store.createIndex('entityType', 'entityType', { unique: false })
          store.createIndex('timestamp', 'metadata.timestamp', {
            unique: false,
          })
        }
      }
    })
  }

  private async ensureDb(): Promise<IDBDatabase> {
    if (!this.db) {
      await this.init()
    }
    if (!this.db) {
      throw new Error('Failed to initialize IndexedDB')
    }
    return this.db
  }

  /**
   * Save photos for an entity
   */
  async savePhotos(data: SavePhotosInput): Promise<void> {
    const db = await this.ensureDb()

    // Create persistent blob URLs from files for offline preview
    const blobUrls =
      data.blobUrls || data.files.map((file) => URL.createObjectURL(file))

    const storedPhoto: StoredPhoto = {
      ...data,
      blobUrls,
      metadata: {
        ...data.metadata,
        timestamp: Date.now(),
      },
    }

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.put(storedPhoto)

      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  /**
   * Get photos for a specific entity
   */
  async getPhotos(id: string): Promise<StoredPhoto | null> {
    const db = await this.ensureDb()

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.get(id)

      request.onsuccess = () => resolve(request.result || null)
      request.onerror = () => reject(request.error)
    })
  }

  /**
   * Get all pending photos
   */
  async getAllPendingPhotos(): Promise<StoredPhoto[]> {
    const db = await this.ensureDb()

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.getAll()

      request.onsuccess = () => resolve(request.result || [])
      request.onerror = () => reject(request.error)
    })
  }

  /**
   * Delete photos for a specific entity
   */
  async deletePhotos(id: string): Promise<void> {
    const db = await this.ensureDb()

    // First get the photos to revoke blob URLs
    const storedPhoto = await this.getPhotos(id)
    if (storedPhoto?.blobUrls) {
      storedPhoto.blobUrls.forEach((url) => {
        try {
          URL.revokeObjectURL(url)
        } catch (e) {
          captureException(e, {
            extra: {
              url,
            },
          })
          // Blob URL might already be revoked
        }
      })
    }

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.delete(id)

      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  /**
   * Clear all stored photos
   */
  async clearAll(): Promise<void> {
    const db = await this.ensureDb()

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.clear()

      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  /**
   * Get count of pending photos
   */
  async getCount(): Promise<number> {
    const db = await this.ensureDb()

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.count()

      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }
}

export const offlinePhotoStorage = new OfflinePhotoStorage()
